import { useEffect, useRef, useCallback } from "react";

/**
 * usePoll — the one way to poll an endpoint from a component.
 *
 * Replaces bare `setInterval(fetchX, ms)`, which the Sep 2026 performance
 * audit found stacking requests platform-wide: a fixed-cadence interval keeps
 * firing while the previous request is still in flight, so the moment the
 * backend slows down every open tab multiplies its own load (a 4 s poller
 * can pile 7 requests deep) — positive feedback straight into Cloud Run's
 * per-instance concurrency cap. This hook:
 *
 *   - never overlaps: the next tick is scheduled only after `fn` settles
 *   - pauses while the tab is hidden and fires immediately on return
 *     (browsers throttle timers in background tabs anyway; this makes the
 *     "stale until the next tick" gap disappear and stops idle tabs from
 *     billing the server)
 *   - backs off on failure (`fn` throws or resolves `false`): 2× per
 *     consecutive failure up to `maxBackoff` × interval, resets on success
 *   - jitters each delay ±`jitter` so a fleet of tabs never fires in lockstep
 *   - always calls the LATEST `fn` (kept in a ref) so callers can pass an
 *     unstable closure without re-arming the timer on every render
 *
 * @param {() => (Promise<any>|any)} fn  the poll body; return `false` to
 *        signal failure (a non-ok response) so the hook backs off
 * @param {number} intervalMs
 * @param {object} [opts]
 * @param {boolean} [opts.enabled=true]        false = fully stopped
 * @param {boolean} [opts.immediate=true]      run once on mount/enable
 * @param {boolean} [opts.pauseWhenHidden=true]
 * @param {number}  [opts.jitter=0.1]          fraction of interval (0 = none)
 * @param {number}  [opts.maxBackoff=5]        max multiplier under failure
 * @param {any}     [opts.resetKey]            change it to run again right away
 * @param {boolean} [opts.refreshOnVisible=false] ALWAYS run when the tab
 *        becomes visible again (default: only if a tick was skipped while
 *        hidden). For live-data pages where "I just came back" must mean
 *        "show me now" — costs one request per return, so opt in deliberately.
 * @returns {() => Promise<void>} a manual "run now" trigger
 */
export default function usePoll(
  fn,
  intervalMs,
  { enabled = true, immediate = true, pauseWhenHidden = true, jitter = 0.1, maxBackoff = 5, resetKey, refreshOnVisible = false } = {},
) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  // Cadence can change between renders (e.g. slow reconcile while a socket is
  // live, fast polling when it drops) — read the latest at each schedule.
  const cfgRef = useRef({ intervalMs, jitter, maxBackoff });
  cfgRef.current = { intervalMs, jitter, maxBackoff };
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);
  const pendingRef = useRef(false); // a run was requested while one was in flight
  const failuresRef = useRef(0);

  const runOnce = useCallback(async () => {
    if (inFlightRef.current) { pendingRef.current = true; return; }
    inFlightRef.current = true;
    try {
      const result = await fnRef.current();
      failuresRef.current = result === false ? failuresRef.current + 1 : 0;
    } catch {
      failuresRef.current += 1;
    } finally {
      inFlightRef.current = false;
    }
    // A re-arm (filter change, manual refresh) landed mid-request: run again
    // now with the LATEST fn, so the new filter's data replaces the old.
    if (pendingRef.current) {
      pendingRef.current = false;
      await runOnce();
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    // Per-effect liveness: a tick that was awaiting a request when this effect
    // was torn down must NOT reschedule into the next effect's chain.
    let alive = true;
    const hidden = () => pauseWhenHidden && typeof document !== "undefined" && document.hidden;

    const schedule = () => {
      if (!alive) return;
      if (timerRef.current) clearTimeout(timerRef.current); // one chain, ever
      const { intervalMs: every, jitter: jit, maxBackoff: cap } = cfgRef.current;
      const backoff = Math.min(cap, 2 ** failuresRef.current);
      const spread = jit ? 1 + (Math.random() * 2 - 1) * jit : 1;
      timerRef.current = setTimeout(tick, Math.max(250, every * backoff * spread));
    };
    const tick = async () => {
      if (!alive) return;
      if (hidden()) { timerRef.current = null; return; } // resumes on visibilitychange
      await runOnce();
      if (!alive) return;
      schedule();
    };
    const onVisible = () => {
      if (!alive || hidden()) return;
      if (refreshOnVisible) {
        // Replace whatever is pending with an immediate run; tick re-schedules.
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        tick();
        return;
      }
      if (timerRef.current) return; // a tick is pending or running — it will land
      tick();
    };

    if (pauseWhenHidden && typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisible);
    }
    if (immediate) tick(); else schedule();

    return () => {
      alive = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      if (pauseWhenHidden && typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisible);
      }
    };
    // A cadence change re-arms immediately (a live socket dropping to polling
    // must not wait out a slow tick); jitter/maxBackoff are read per schedule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, immediate, pauseWhenHidden, refreshOnVisible, resetKey, intervalMs, runOnce]);

  return runOnce;
}
